import clientConfigs from 'app/config/client';
import { ClientConfigs } from 'app/config/ClientConfigs';
import { BASE_FONT_NAMES, CONTENT_BASE_FONT_NAME, ICONS_FONT_NAME } from 'app/constants/Fonts';
import { CLIENT_VERSION } from 'app/constants/PageMetaTags';
import { fontList } from 'app/utils/text';
import React from 'react';
import { useClasses } from 'app/utils/css';
import { firefoxPrintHackStyles } from 'app/constants/Styles';

export interface IProps {
  bundleScripts: string[];
  assetBaseURL: string;
  noscriptMessages?: string[];
  version?: string;
  serverRenderedContent?: {
    html: string,
    metaElements: React.ReactElement[],
    styleElements: React.ReactElement[],
  };
  lang?: string;
}

const Index = ({
  bundleScripts,
  assetBaseURL,
  noscriptMessages = ['Please enable JavaScript to view this page.'],
  version,
  serverRenderedContent,
  lang,
}: IProps) => {
  const style = `
    @font-face {
      font-style: normal;
      font-family: '${CONTENT_BASE_FONT_NAME}';
      font-weight: 400;
      src: local('Open Sans'), url('${assetBaseURL}/fonts/OpenSans-Regular.woff2') format('woff2');
    }

    @font-face {
      font-style: normal;
      font-family: '${CONTENT_BASE_FONT_NAME}';
      font-weight: 600;
      src: local('Open Sans Semibold'),
           url('${assetBaseURL}/fonts/OpenSans-Semibold.woff2') format('woff2');
    }

    @font-face {
      font-style: normal;
      font-family: '${CONTENT_BASE_FONT_NAME}';
      font-weight: 300;
      src: local('Open Sans Light'), url('${assetBaseURL}/fonts/OpenSans-Light.woff2') format('woff2');
    }

    @font-face {
      font-family: '${ICONS_FONT_NAME}';
      font-style: normal;
      font-weight: 400;
      src: url('${assetBaseURL}/fonts/eikaiwa-icons.woff') format('woff');
      unicode-range: U+e9??;
    }

    html, body, #app-container {
      min-height: 100vh;
      font-family: ${fontList(BASE_FONT_NAMES)};
    }`;

  const classes = useClasses({
    appContainer: {
      display: 'flex',
      flexDirection: 'column',
      '@media print': firefoxPrintHackStyles,
    },
  });

  return (
    <html lang={lang}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name={CLIENT_VERSION} content={version} />

        {serverRenderedContent && serverRenderedContent.metaElements}

        <style dangerouslySetInnerHTML={{ __html: style }} />
        {serverRenderedContent && serverRenderedContent.styleElements}
      </head>
      <body>
        <div id="app-container" className={classes.appContainer}>
          {serverRenderedContent ? (
            <noscript dangerouslySetInnerHTML={{ __html: serverRenderedContent.html }} />
          ) : (
            <noscript>
              <div>
                {
                  noscriptMessages.map((noscriptMessage, key) =>
                    <p key="key">{noscriptMessage}</p>)
                }
              </div>
            </noscript>
          )}
        </div>
        <script async src={`${assetBaseURL}/redirect-unsupported-browsers.js`} />
        <script
          dangerouslySetInnerHTML={{
            __html:
              // The client configs must be encoded before stringifying to ensure the client
              // does attempt to double decode at runtime.
              `window.ecfConfig = ${JSON.stringify(ClientConfigs.encode(clientConfigs))};` +
              `window.assetBaseURL = ${JSON.stringify(assetBaseURL)};`,
          }}
        />
        {bundleScripts.map(bundleScript => (
          <script key={bundleScript} src={`${assetBaseURL}/${bundleScript}`} />
        ))}
      </body>
    </html>
  );
};

export default Index;
